import { useState, type ReactNode } from "react";
import { AdsHelpHint } from "@/components/ads-assistant/AdsAssistantCollapsible";
import { AdsAssistantIngestCrmSyncContent } from "@/components/ads-assistant/AdsAssistantIngestCrmSyncContent";

type Props = {
  scopedOrgId: string | null;
  showCrmAdvanced: boolean;
  onAfterSync?: () => void;
  children: ReactNode;
};

/** Manual ingestion shell: header row with optional Advanced pill; swaps form for CRM sync panel. */
export function AdsAssistantManualIngestionBox({ scopedOrgId, showCrmAdvanced, onAfterSync, children }: Props) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/90 dark:border-gray-600 dark:bg-zinc-900/40">
      <div
        className={`flex items-center justify-between gap-2 px-3 ${advancedOpen ? "py-2" : "pt-3 pb-1"}`}
      >
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          {advancedOpen ? (
            <button
              type="button"
              onClick={() => setAdvancedOpen(false)}
              className="text-left text-xs font-medium text-gray-900 hover:text-gray-700 dark:text-gray-100 dark:hover:text-gray-300"
            >
              Manual ingestion
            </button>
          ) : (
            <>
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100">Manual ingestion</p>
              <AdsHelpHint label="Manual ingestion — field guide">
                Paste the <strong>exact caption or post text</strong> as it appears on Facebook or Google. Pick the{" "}
                <strong>platform</strong> and <strong>time posted</strong>. External id is optional unless mirroring a real
                post id. <strong>Creative description</strong> explains what the image shows (or should). Categories and
                template link the row to your library. Submit runs stub analysis and policy checks.
              </AdsHelpHint>
            </>
          )}
        </div>
        {showCrmAdvanced ? (
          <button
            type="button"
            aria-expanded={advancedOpen}
            onClick={() => setAdvancedOpen((open) => !open)}
            className={
              "shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold transition " +
              (advancedOpen
                ? "border-indigo-400 bg-indigo-100 text-indigo-900 dark:border-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-100"
                : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-200")
            }
          >
            Advanced
          </button>
        ) : null}
      </div>

      {advancedOpen && showCrmAdvanced ? (
        <AdsAssistantIngestCrmSyncContent scopedOrgId={scopedOrgId} onAfterSync={onAfterSync} />
      ) : !advancedOpen ? (
        <div className="px-3 pb-3">{children}</div>
      ) : null}
    </div>
  );
}
